import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { IListaDeduplicacao } from '../../../models';
import { IFormError, IFormProps } from '../../utils';

interface ICreateListaDeduplicacaoFormFields {
    Nome: string;
}

interface IEditListaDeduplicacaoFormFields {
    IdVelesGeoListaDeduplicacao: number;
    Nome: string;
    CPFs: string[]
    Status: boolean;
}

const createListaDeduplicacaoFormFieldsInitialState: ICreateListaDeduplicacaoFormFields = {
    Nome: ''
}

const editListaDeduplicacaoFormFieldsInitialState: IEditListaDeduplicacaoFormFields = {
    IdVelesGeoListaDeduplicacao: 0,
    Nome: '',
    Status: false,
    CPFs: []
}

interface IListaDeduplicacaoState {
    matchCode: {
        selectedColumn: string,
        distinctValues: number,
        validValues: number
    },
    fileData: {
        name: string,
        size: string,
        columns: string[],
        type: string,
        data: any[],
        validData?: any[],
        invalidData?: any[]
    },
    searchTerm: string
    listasDeduplicacao: IListaDeduplicacao[]
    listaSelecionada?: IListaDeduplicacao
    fileListData: {
        fileListData: string[]
        fileListTableValues: {
            fileListValidData: string[]
            fileListInvalidData: string[]
            fileListRepeatedData: string[]
        }
    }
    openListasDeduplicacao: boolean
    createListaDeduplicacaoForm: IFormProps<ICreateListaDeduplicacaoFormFields>
    openCreateListaDeduplicacao: boolean
    editListaDeduplicacaoForm: IFormProps<IEditListaDeduplicacaoFormFields>
    openEditListaDeduplicacao: boolean
    openImportFileListaDeduplicacao: boolean
    openPreviewListaDeduplicacao: boolean
    openEditCPF: boolean
}

const initialState: IListaDeduplicacaoState = {
    fileListData: {
        fileListData: [],
        fileListTableValues: {
            fileListInvalidData: [],
            fileListRepeatedData: [],
            fileListValidData: []
        }
    },
    matchCode: {
        selectedColumn: '',
        distinctValues: 0,
        validValues: 0
    },
    fileData: {
        name: '',
        size: '',
        columns: [],
        type: '',
        data: []
    },
    listasDeduplicacao: [],
    searchTerm: '',
    createListaDeduplicacaoForm: {
        errors: [],
        fields: createListaDeduplicacaoFormFieldsInitialState,
        touched: false,
        delivered: false
    },
    openListasDeduplicacao: false,
    openCreateListaDeduplicacao: false,
    editListaDeduplicacaoForm: {
        errors: [],
        fields: editListaDeduplicacaoFormFieldsInitialState,
        touched: false,
        delivered: false
    },
    openEditListaDeduplicacao: false,
    openImportFileListaDeduplicacao: false,
    openPreviewListaDeduplicacao: false,
    openEditCPF: false
};

export const validateCreateListaDeduplicacaoForm = (fields: ICreateListaDeduplicacaoFormFields): IFormError<keyof ICreateListaDeduplicacaoFormFields>[] => {
    const errors: IFormError<keyof ICreateListaDeduplicacaoFormFields>[] = [];

    if (!fields.Nome) {
        errors.push({
            key: 'Nome',
            message: 'O nome da lista não pode ficar em branco'
        })
    } else {
        if (fields.Nome.length === 0 || fields.Nome.replace('  ', ' ').trim().length === 0) {
            errors.push({
                key: 'Nome',
                message: 'O nome da lista não pode ficar em branco'
            })
        }
    }

    return errors;
}

export const validateEditListaDeduplicacaoForm = (fields: IEditListaDeduplicacaoFormFields): IFormError<keyof IEditListaDeduplicacaoFormFields>[] => {
    const errors: IFormError<keyof IEditListaDeduplicacaoFormFields>[] = [];

    if (!fields.Nome) {
        errors.push({
            key: 'Nome',
            message: 'Nome é obrigatório',
        });
    } else {
        if (fields.Nome.length === 0 || fields.Nome.replace('  ', ' ').trim().length === 0) {
            errors.push({
                key: 'Nome',
                message: 'O nome do projeto não pode ficar em branco'
            })
        }
    }

    return errors;
}

const listaDeduplicacaoSlice = createSlice({
    name: 'listaDeduplicacao',
    initialState,
    reducers: {
        resetListaDeduplicacaoState(state) {
            state = initialState
        },
        setListasDeduplicacaoState(state, action: PayloadAction<{ listasDeduplicacao: IListaDeduplicacao[] }>) {
            state.listasDeduplicacao = action.payload.listasDeduplicacao
        },
        resetListasDeduplicacaoState(state) {
            state.listasDeduplicacao = initialState.listasDeduplicacao
        },
        setSelectedListasDeduplicacaoState(state, action: PayloadAction<{ listaDeduplicacao: IListaDeduplicacao }>) {
            state.listaSelecionada = action.payload.listaDeduplicacao
        },
        // Create Lista Deduplicacao
        setCreateListaDeduplicacaoNomeState(state, action: PayloadAction<{ Nome: string }>) {
            state.createListaDeduplicacaoForm.fields.Nome = action.payload.Nome
        },
        setCreateListaDeduplicacaoFormTouchedState(state, action: PayloadAction<{ touched: boolean }>) {
            state.createListaDeduplicacaoForm.touched = action.payload.touched;
        },
        setCreateListaDeduplicacaoFormErrorsState(state, action: PayloadAction<{ errors: IFormError<keyof ICreateListaDeduplicacaoFormFields>[] }>) {
            state.createListaDeduplicacaoForm.errors = action.payload.errors;
        },
        setCreateListaDeduplicacaoFormDeliveredState(state, action: PayloadAction<{ delivered: boolean }>) {
            state.createListaDeduplicacaoForm.delivered = action.payload.delivered;
        },
        resetCreateListaDeduplicacaoFormState(state) {
            state.createListaDeduplicacaoForm = initialState.createListaDeduplicacaoForm
        },
        // Edit Lista Deduplicacao
        setEditListaDeduplicacaoIdVelesGeoListaDeduplicacaoState(state, action: PayloadAction<{ IdVelesGeoListaDeduplicacao: number }>) {
            state.editListaDeduplicacaoForm.fields.IdVelesGeoListaDeduplicacao = action.payload.IdVelesGeoListaDeduplicacao
        },
        setEditListaDeduplicacaoNomeState(state, action: PayloadAction<{ nome: string }>) {
            state.editListaDeduplicacaoForm.fields.Nome = action.payload.nome
        },
        setEditListaDeduplicacaoAtivaState(state, action: PayloadAction<{ Status: boolean }>) {
            state.editListaDeduplicacaoForm.fields.Status = action.payload.Status
        },
        setCPFsEditListaDeduplicacaoState(state, action: PayloadAction<{ CPFs: string[] }>) {
            state.editListaDeduplicacaoForm.fields.CPFs = action.payload.CPFs
        },
        setAddCPFListEditListaDeduplicacaoState(state, action: PayloadAction<{ CPFs: string[] }>) {
            state.editListaDeduplicacaoForm.fields.CPFs = [...state.editListaDeduplicacaoForm.fields.CPFs, ...action.payload.CPFs]
        },
        setEditListaDeduplicacaoAddCPFState(state, action: PayloadAction<{ CPF: string }>) {
            if (state.editListaDeduplicacaoForm.fields.CPFs.includes(action.payload.CPF)) {
                return
            }

            state.editListaDeduplicacaoForm.fields.CPFs.push(action.payload.CPF)
        },
        setEditListaDeduplicacaoRemoveCPFState(state, action: PayloadAction<{ CPF: string }>) {
            state.editListaDeduplicacaoForm.fields.CPFs = state.editListaDeduplicacaoForm.fields.CPFs.filter(item => item !== action.payload.CPF)
        },
        setEditListaDeduplicacaoEditCPFState(state, action: PayloadAction<{
            oldCPF: string,
            newCPF: string
        }>) {
            if (state.editListaDeduplicacaoForm.fields.CPFs.includes(action.payload.newCPF)) {
                return
            }

            state.editListaDeduplicacaoForm.fields.CPFs = state.editListaDeduplicacaoForm.fields.CPFs.map(item => item === action.payload.oldCPF ? action.payload.newCPF : item)
        },
        setEditListaDeduplicacaoFormTouchedState(state, action: PayloadAction<{ touched: boolean }>) {
            state.editListaDeduplicacaoForm.touched = action.payload.touched;
        },
        setEditListaDeduplicacaoFormErrorsState(state, action: PayloadAction<{ errors: IFormError<keyof IEditListaDeduplicacaoFormFields>[] }>) {
            state.editListaDeduplicacaoForm.errors = action.payload.errors;
        },
        setEditListaDeduplicacaoFormDeliveredState(state, action: PayloadAction<{ delivered: boolean }>) {
            state.editListaDeduplicacaoForm.delivered = action.payload.delivered;
        },
        resetEditListaDeduplicacaoFormState(state) {
            state.editListaDeduplicacaoForm = initialState.editListaDeduplicacaoForm
        },
        setListaDeduplicacaoFileDataListState(state, action: PayloadAction<{ value: string[] }>) {
            state.fileListData.fileListData = action.payload.value
        },
        setListaDeduplicacaoFileValidDataListState(state, action: PayloadAction<{ value: string[] }>) {
            state.fileListData.fileListTableValues.fileListValidData = action.payload.value
        },
        setListaDeduplicacaoFileRepeatedDataListState(state, action: PayloadAction<{ value: string[] }>) {
            state.fileListData.fileListTableValues.fileListRepeatedData = action.payload.value
        },
        setListaDeduplicacaoFileInvalidDataListState(state, action: PayloadAction<{ value: string[] }>) {
            state.fileListData.fileListTableValues.fileListInvalidData = action.payload.value
        },
        setListaDeduplicacaoSelectedColumnState(state, action: PayloadAction<{
            selectedColumn: string
        }>) {
            state.matchCode.selectedColumn = action.payload.selectedColumn
        },
        setListaDeduplicacaoDistinctValuesState(state, action: PayloadAction<{
            distinctValues: number
        }>) {
            state.matchCode.distinctValues = action.payload.distinctValues
        },
        setListaDeduplicacaoValidValuesState(state, action: PayloadAction<{
            validValues: number
        }>) {
            state.matchCode.validValues = action.payload.validValues
        },
        setListaDeduplicacaoFileDataState(state, action: PayloadAction<{
            fileData: {
                name: string,
                size: string,
                columns: string[],
                type: string,
                data: any[],
                validData?: any[],
                invalidData?: any[]
            }
        }>) {
            state.fileData = action.payload.fileData
        },
        resetListaDeduplicacaoFileDataState(state) {
            state.fileData = initialState.fileData
            state.matchCode = initialState.matchCode
            state.fileListData = initialState.fileListData
        },
        setOpenCreateListaDeduplicacaoState(state, action: PayloadAction<boolean>) {
            state.openCreateListaDeduplicacao = action.payload
        },
        setOpenEditListaDeduplicacaoState(state, action: PayloadAction<boolean>) {
            state.openEditListaDeduplicacao = action.payload
        },
        setOpenImportFileDeduplicacaoState(state, action: PayloadAction<boolean>) {
            state.openImportFileListaDeduplicacao = action.payload
        },
        setOpenPreviewListaDeduplicacaoState(state, action: PayloadAction<boolean>) {
            state.openPreviewListaDeduplicacao = action.payload
        },
        setOpenEditCPFState(state, action: PayloadAction<boolean>) {
            state.openEditCPF = action.payload
        },
        setOpenListasDeduplicacaoState(state, action: PayloadAction<boolean>) {
            state.openListasDeduplicacao = action.payload
        }
    }
});

export const {
    resetCreateListaDeduplicacaoFormState,
    resetEditListaDeduplicacaoFormState,
    resetListasDeduplicacaoState,
    resetListaDeduplicacaoFileDataState,
    resetListaDeduplicacaoState,
    setAddCPFListEditListaDeduplicacaoState,
    setCPFsEditListaDeduplicacaoState,
    setCreateListaDeduplicacaoFormDeliveredState,
    setCreateListaDeduplicacaoFormErrorsState,
    setCreateListaDeduplicacaoFormTouchedState,
    setCreateListaDeduplicacaoNomeState,
    setEditListaDeduplicacaoAddCPFState,
    setEditListaDeduplicacaoAtivaState,
    setEditListaDeduplicacaoEditCPFState,
    setEditListaDeduplicacaoFormDeliveredState,
    setEditListaDeduplicacaoFormErrorsState,
    setEditListaDeduplicacaoFormTouchedState,
    setEditListaDeduplicacaoIdVelesGeoListaDeduplicacaoState,
    setEditListaDeduplicacaoNomeState,
    setEditListaDeduplicacaoRemoveCPFState,
    setListaDeduplicacaoDistinctValuesState,
    setListaDeduplicacaoFileDataListState,
    setListaDeduplicacaoFileDataState,
    setListaDeduplicacaoFileInvalidDataListState,
    setListaDeduplicacaoFileRepeatedDataListState,
    setListaDeduplicacaoFileValidDataListState,
    setListaDeduplicacaoSelectedColumnState,
    setListaDeduplicacaoValidValuesState,
    setListasDeduplicacaoState,
    setSelectedListasDeduplicacaoState,
    setOpenCreateListaDeduplicacaoState,
    setOpenEditListaDeduplicacaoState,
    setOpenImportFileDeduplicacaoState,
    setOpenPreviewListaDeduplicacaoState,
    setOpenEditCPFState,
    setOpenListasDeduplicacaoState
} = listaDeduplicacaoSlice.actions;

export const listaDeduplicacaoReducer = listaDeduplicacaoSlice.reducer;